// Shared types for the app.

export interface AuthUser {
  name: string;
  email: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  roles?: string[];         // "Admin" | "Finance" - from /api/auth/profile
  /** True iff at least one other user's reporting manager (from Entra) is
   *  this user - the sole condition for showing My Approvals in the nav. */
  isManager?: boolean;
  /** True iff THIS user has a reporting manager of their own on file. False
   *  means a travel request or advance they raise skips the Manager-approval
   *  stage entirely and goes straight to PendingFinance (see
   *  requests_api.py's _route_for_manager_approval) - used to show the
   *  "Notify Finance" Cc popup (FinanceNoticeDialog) before they submit,
   *  since there's no separate Manager-approve step to show it at
   *  afterwards. */
  hasReportingManager?: boolean;
  /** Display-only name of the reporting manager hasReportingManager refers
   *  to - null when there is none, or when it couldn't be resolved. */
  reportingManagerName?: string | null;
}

// --- Chat/agent types - DORMANT (see CLAUDE.md). Kept for a later phase,
// unused by the admin portal's own pages. ---

export interface Agent {
  id: string;
  name: string;
  description: string;
  icon: string;            // lucide-react icon name
  suggestions: string[];
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  ts?: string;
}

// Conversation summary (list view) and full conversation (with messages).
export interface ConversationSummary {
  id: string;
  agentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface Conversation extends Omit<ConversationSummary, 'messageCount'> {
  messages: ChatMessage[];
}

// --- Admin portal types (active) ---

export interface OrgUser {
  id: string;
  email: string;
  displayName: string;
  department?: string | null;
  designation?: string | null;
  reportingManagerId?: string | null;
  /** Resolved server-side from Graph - the local id→name map can't cover
   *  managers who have never signed in to this app themselves. */
  reportingManagerName?: string | null;
  roles: string[];
}

/** Admin-configured (see Policies.tsx's "Expense Categories" panel,
 *  fetchExpenseCategories/updateExpenseCategories in api.ts) - a plain
 *  string, not a closed union anymore. The string IS the category as
 *  stored on a bill/policy rate; there is no separate key/label pair (see
 *  db/policy_store.py's "Expense categories are admin-configurable"
 *  module docstring section). */
export type ExpenseCategory = string;

/** Fixed Allowance and Reimbursement are two separate schemes, not parts of
 *  one another - a flat amount paid without bills, vs actual expenses
 *  claimed against per-category caps. An employee's request uses one or the
 *  other. */
export type PolicyScheme = 'fixed_allowance' | 'reimbursement';

/** Which rate of a country row applies to a trip, chosen by the employee
 *  when they raise a request or advance - Domestic (a trip within one
 *  country, entered with a start and end city) or International (a trip from
 *  a starting country to a destination country). It is an explicit choice,
 *  not inferred from where the employee lives: the app is used worldwide.
 *  See db/policy_store.py. */
export type TravelType = 'domestic' | 'international';

export const TRAVEL_TYPES: TravelType[] = ['domestic', 'international'];

export const TRAVEL_TYPE_LABEL: Record<TravelType, string> = {
  domestic: 'Domestic',
  international: 'International',
};

/** A flat amount means nothing without a currency - 10,000 INR and 10,000
 *  USD are very different limits. Every rate therefore carries its own
 *  currency. */
export interface AmountRate {
  currency: string;
  amount: number;
}

/** A Reimbursement rate's per-category, per-day limits. A number is a
 *  "Limit" (0 genuinely means zero allowed); `null` is "No limit" - the
 *  employee can claim whatever they spent in that category, it never shows
 *  an over-limit indicator, and it adds nothing to the trip's estimated
 *  total (see db/policy_store.py's module docstring). */
export type CategoryLimits = Record<string, number | null>;

export interface LimitsRate {
  currency: string;
  limits: CategoryLimits;
}

/** One country's Fixed Allowance rates - Domestic (trips within it) and/or
 *  International (trips to it from another country); at least one. There is
 *  no fallback/catch-all - a country with no rate for the travel type an
 *  employee picks simply isn't offered to them (see fetchAllowedCountries
 *  in api.ts). */
export interface CountryAmount {
  country: string;
  domestic?: AmountRate;
  international?: AmountRate;
}

/** One country's Reimbursement per-category limits - Domestic and/or
 *  International (at least one), same meaning as CountryAmount above. */
export interface CountryLimits {
  country: string;
  domestic?: LimitsRate;
  international?: LimitsRate;
}

export interface FixedAllowanceBucket {
  countries: CountryAmount[];
}

export interface ReimbursementBucket {
  countries: CountryLimits[];
}

export interface FixedAllowanceEntry extends FixedAllowanceBucket {
  designation: string;
}

export interface ReimbursementEntry extends ReimbursementBucket {
  designation: string;
}

export interface PolicySheet {
  id: string;
  version: number;
  isActive: boolean;
  /** A trip's leftover hours (after every complete 24h block) count as a
   *  Full Day at/above this threshold, a Half Day below it - see
   *  db/travel_duration.py. No independent half-day value anymore. */
  fullDayHourThreshold: number;
  /** Org-wide countries used only for a designation with no countries of
   *  its own (for the chosen Domestic/International rate) in
   *  fixedAllowances / reimbursements below. Resolution order: designation's
   *  own countries -> this default's countries -> LookupError (no
   *  fallback/catch-all). */
  defaultFixedAllowance: FixedAllowanceBucket;
  defaultReimbursementLimits: ReimbursementBucket;
  fixedAllowances: FixedAllowanceEntry[];
  reimbursements: ReimbursementEntry[];
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** One entry returned by GET /my-policy/countries - a country the
 *  signed-in employee can actually pick for a given travel type (and, for a
 *  request, claim method), per PolicyStore.list_countries_for's resolution
 *  chain. The request/advance forms use this to restrict their Country
 *  picker so a submission can never fail with "not configured" (see
 *  requests_api.py's list_allowed_countries). */
export interface AllowedCountry {
  country: string;
  currency: string;
}

/** "PendingFinance" is the stored/technical name (matches the backend's
 *  STATUSES tuple) - display it via TRAVEL_REQUEST_STATUS_LABEL below,
 *  which says "Pending Finance Approver", matching this app's existing
 *  Finance→Finance Approver UI relabel (see lib/roles.ts). Approved means
 *  the FINAL approval (Finance Approver signed off), not just the
 *  Manager's - see rejectedBy for which stage produced a Rejected status.
 *  ResubmitRequested means a Manager or Finance Approver sent the request
 *  back to the employee to fix instead of deciding it - see
 *  resubmitRequestedBy for which stage asked for it (and therefore which
 *  stage it returns to once resubmitted). */
export type TravelRequestStatus = 'Draft' | 'Submitted' | 'PendingFinance' | 'Approved' | 'Rejected' | 'Completed' | 'ResubmitRequested';

export const TRAVEL_REQUEST_STATUS_LABEL: Record<TravelRequestStatus, string> = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  PendingFinance: 'Pending',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Completed: 'Completed',
  ResubmitRequested: 'Needs changes',
};

export type ClaimMethod = 'fixed_allowance' | 'reimbursement';

export const CLAIM_METHOD_LABEL: Record<ClaimMethod, string> = {
  fixed_allowance: 'Fixed Allowance',
  reimbursement: 'Actual Amount',
};

/** "other" marks the Other reimbursements flow (City travel /
 *  Miscellaneous) - same request/advance documents as travel, with no trip.
 *  Absent on every travel request/advance. */
export type RequestKind = 'other';

export type OtherClaimType = 'city_travel' | 'miscellaneous';

export const OTHER_CLAIM_TYPES: OtherClaimType[] = ['city_travel', 'miscellaneous'];

export const OTHER_CLAIM_TYPE_LABEL: Record<OtherClaimType, string> = {
  city_travel: 'City travel',
  miscellaneous: 'Miscellaneous',
};

/** A request's Type, as the approval queues show it: "Travel" for every
 *  travel request, otherwise its Other claim type. */
export function requestTypeLabel(row: { requestKind?: RequestKind | null; claimType?: OtherClaimType | null }): string {
  if (row.requestKind !== 'other') return 'Travel';
  return row.claimType ? OTHER_CLAIM_TYPE_LABEL[row.claimType] : 'Other';
}

/** Which set of fields a bill form/card shows - "travel" for every travel
 *  request (unchanged), or an Other claim type's own fields. */
export type BillFields = 'travel' | OtherClaimType;

export interface ExpenseLineItem {
  id: string;
  category: ExpenseCategory;
  amount: number;
  /** ISO 4217 code (e.g. "SGD", "USD") - set per bill, independent of the
   *  request's own overall currency (used only for the policy limit/
   *  allowance preview). Falls back to the request's currency for bills
   *  uploaded before this field existed. */
  currency: string;
  /** Merchant/vendor name, carried through from AI extraction (or typed by
   *  hand) purely so later duplicate-bill checks have a strong signal to
   *  compare against - not shown as its own editable field in the wizard,
   *  same treatment as the AI-extracted description. Null for bills
   *  uploaded before this field existed, or a manual entry with none typed. */
  vendor?: string | null;
  description?: string | null;
  /** The bill's own transaction date (ISO 8601, optional) - distinct from
   *  uploadedAt (when it was added to this request). Fillable by hand or
   *  via the "Analyze with AI" auto-fill agent. */
  date?: string | null;
  /** All three are null together for a manually-entered bill with no
   *  attached receipt (see ExpensesStep.tsx's manual-add path) - every
   *  other bill (dropped file, AI-analyzed or not) always has all three. */
  blobPath: string | null;
  filename: string | null;
  contentType: string | null;
  uploadedAt: string;
  /** How much this bill exceeds its category's policy limit by, in the
   *  bill's own currency - an indicator only, never a block (see
   *  requests_api.py's _exceeds_policy_by). Null when within policy, the
   *  claim method is Fixed Allowance (no per-category limits), or the
   *  bill's currency doesn't match the snapshotted limit's currency (no
   *  meaningful comparison possible without an FX rate). */
  exceedsPolicyBy?: number | null;
  /** Other reimbursement bills only - City travel: startPlace, endPlace,
   *  purpose, modeOfTransport (optional), buyer; Miscellaneous: buyer (and
   *  the shared description). Never set on a travel bill. */
  startPlace?: string | null;
  endPlace?: string | null;
  purpose?: string | null;
  modeOfTransport?: string | null;
  buyer?: string | null;
}

/** The Other-reimbursement bill fields, as sent to the upload/edit routes. */
export interface OtherBillFieldValues {
  startPlace?: string;
  endPlace?: string;
  purpose?: string;
  modeOfTransport?: string;
  buyer?: string;
}

/** A plain supporting attachment on a request (visa, boarding pass,
 *  itinerary, hotel confirmation, etc.) - reference only, with an optional
 *  note. Unlike ExpenseLineItem it carries no amount/category and never
 *  touches the allowance, policy limits or the duplicate check (see
 *  requests_api.py's document routes). A document always has a file, so the
 *  three blob fields are never null together the way an ExpenseLineItem's
 *  can be for a manual bill. */
export interface RequestDocument {
  id: string;
  note?: string | null;
  blobPath: string;
  filename: string | null;
  contentType: string | null;
  uploadedAt: string;
}

/** A manually-entered FX rate for one (currency, targetCurrency) pair in a
 *  request's multi-currency reconciliation ledger (see lib/fx.ts,
 *  CurrencyConversionCard.tsx) - used in place of the fetched Frankfurter
 *  rate, e.g. to match the employee's actual bank/credit-card conversion
 *  rate. Anyone viewing the ledger can set one (requester, Manager, Finance,
 *  Admin), with an optional receipt as proof; saved immediately, so it's
 *  visible to everyone else who opens this request from that point on, and
 *  recorded in the audit log. The receipt is optional (unlike
 *  RequestDocument, which always has a file) - an override can be
 *  justified by the typed reason alone. */
export interface FxOverride {
  id: string;
  currency: string;
  targetCurrency: string;
  rate: number;
  reason?: string | null;
  receiptBlobPath?: string | null;
  filename?: string | null;
  contentType?: string | null;
  setBy: string;
  setByName?: string | null;
  setAt: string;
}

/** One entry in the array returned by POST .../expenses/analyze - the AI
 *  agent's best-guess reading of one bill, for the user to review/edit
 *  before it's actually added as an expense (see agents/bill_analysis.py).
 *  A single uploaded file can yield more than one entry when it contains
 *  multiple distinct receipts (e.g. several photographed together). */
export interface ExtractedBillFields {
  vendor: string | null;
  amount: number | null;
  currency: string | null;
  date: string | null;
  category: ExpenseCategory;
  description: string | null;
  confidence: number;
  isLegible: boolean;
}

/** Returned by POST .../expenses/check-duplicate. */
export interface DuplicateVerdict {
  isDuplicate: boolean;
  matchedIndex: number | null;
  reason: string;
  matchedRequestId: string | null;
  matchedFilename: string | null;
  /** True when the match is against another pending card in the SAME
   *  upload batch (not yet added) rather than an already-persisted bill -
   *  matchedFilename is null in that case, since no real file/expense
   *  record exists for it yet. */
  matchedInBatch: boolean;
  /** True when the matched bill was claimed by a DIFFERENT employee -
   *  matchedFilename/matchedRequestId are both null in that case (see
   *  requests_api.py's check_expense_duplicate), so the message shown
   *  stays generic rather than exposing another employee's file/request. */
  matchedCrossEmployee: boolean;
}

export interface RequesterInfo {
  displayName: string | null;
  email: string | null;
  /** Only populated on Advance's requester block (see AdvanceDetailDialog)
   *  - the requester's current designation, live from their Entra profile.
   *  Travel requests use designationAtSubmission instead (a snapshot at
   *  submission time, which is what the policy was actually resolved
   *  against - more accurate than a live lookup for a decision record). */
  designation?: string | null;
}

/** One approver's Review action (edit fields, save, approve in one action) -
 *  shared shape for both a travel request's and an advance's own
 *  reviewEdit field. `fields` lists only the fields whose value actually
 *  changed, not everything the Review dialog's form submitted. */
export interface ReviewEditEntry {
  editedBy: string;
  editedByName: string | null;
  fields: string[];
  editedAt: string;
}

/** A request/advance's reviewEdit field, keyed by which stage reviewed it -
 *  a Manager's Review and a later Finance Review both stay visible rather
 *  than the second overwriting the first. */
export type ReviewEdit = Partial<Record<"Manager" | "Finance", ReviewEditEntry>>;

export interface TravelRequestSummary {
  id: string;
  userId: string;
  /** Set on an Other reimbursement (City travel / Miscellaneous): no trip,
   *  so departureAt/arrivalAt/travelType are null and flightDetails is the
   *  claim's purpose. */
  requestKind?: RequestKind | null;
  claimType?: OtherClaimType | null;
  /** Only present on the single-request detail fetch (GET
   *  /api/travel-requests/:id) that backs RequestDetailDialog - the list
   *  endpoints (My Requests/My Approvals/Finance Queue) don't resolve it,
   *  since those rows already show the requester via other means or don't
   *  need it. */
  requester?: RequesterInfo;
  department: string;
  /** The country the policy rate is keyed by: the one country travelled
   *  within for a Domestic trip, the destination for an International one. */
  country: string;
  /** The employee's explicit Domestic/International choice, which decides
   *  the rate and which of the location fields below apply. Always present
   *  on a fresh read of a single request (older documents get theirs
   *  inferred server-side); may be absent on list rows of older ones. */
  travelType?: TravelType | null;
  /** International only - the starting country. */
  originCountry?: string | null;
  /** Domestic only - where the trip starts and ends, within `country`. */
  startCity?: string | null;
  endCity?: string | null;
  startDate: string;
  endDate: string;
  departureAt: string;
  arrivalAt: string;
  flightDetails?: string | null;
  currency: string;
  currencyAutoSelected: boolean;
  claimMethod: ClaimMethod;
  /** An advance is raised/approved/paid as its own standalone entity (see
   *  Advance below) - this is a reference to one the employee already had
   *  Paid, of the same travel type, linked at submission time (either claim
   *  method). */
  linkedAdvanceId?: string | null;
  /** Uploaded bills for the Reimbursement claim method - entered/edited by
   *  hand for now; the auto-fill and duplicate-detection agents are UI-only
   *  placeholders until wired up later. */
  expenses: ExpenseLineItem[];
  /** Plain supporting attachments (visa, boarding pass, itinerary, etc.) -
   *  reference files only, no amount/category, never affecting the allowance
   *  or policy. See RequestDocument. Absent on documents from before this
   *  field existed, so callers default to []. */
  documents?: RequestDocument[];
  /** Manually-entered FX rates for the multi-currency reconciliation ledger
   *  (see FxOverride, lib/fx.ts) - absent/empty on a request with no
   *  overrides set. */
  fxOverrides?: FxOverride[];
  /** Each reconciliation-ledger row's chosen settlement currency, keyed by
   *  its own currency (e.g. {"SGD": "AFN"}) - without this, a row's target
   *  picker lived only in CurrencyConversionCard's own component state, so
   *  every choice reverted to the request's own currency the moment the
   *  wizard unmounted. Saved immediately on change (setSettlementTarget in
   *  api.ts), same "persist right away" model as fxOverrides. A currency
   *  with no entry here defaults to defaultTargetCurrency, same as before. */
  settlementTargetByCurrency?: Record<string, string>;
  designationAtSubmission?: string | null;
  /** The Finance Approver the requester picked at submission time (see
   *  NewRequestPayload.financeApproverId in api.ts) - who this request
   *  routes to once it reaches PendingFinance. Name/email are snapshotted
   *  alongside the id so the detail timeline can name the Finance stage
   *  before `financeApproval` exists (it's only created once the Manager
   *  approves). Null on records raised before this was captured. */
  financeApproverIdAtSubmission?: string | null;
  financeApproverNameAtSubmission?: string | null;
  financeApproverEmailAtSubmission?: string | null;
  fullDayCount: number;
  halfDayCount: number;
  estimatedAllowanceAmount: number;
  policyVersionAtSubmission?: number | null;
  /** Reimbursement only: the per-day category limits per currency,
   *  snapshotted when the request was created (null on Fixed Allowance and
   *  on requests from before the snapshot existed). Read to know which
   *  categories are "No limit" (null) when comparing bills to the policy. */
  policyLimitsByCurrencyAtSubmission?: Record<string, CategoryLimits> | null;
  status: TravelRequestStatus;
  approval?: Approval | null;
  financeApproval?: Approval | null;
  rejectedBy?: 'Manager' | 'Finance' | null;
  /** Set together with status === 'ResubmitRequested' - which stage sent
   *  it back (and therefore which stage it returns to once the employee
   *  resubmits) and their note on what to fix. Both persist after the
   *  resubmit cycle completes (status moves on), as a record of it having
   *  happened - not cleared back to null. */
  resubmitRequestedBy?: 'Manager' | 'Finance' | null;
  resubmitComment?: string | null;
  /** Set by an approver's Review action (edit fields, save, approve in one
   *  action) - who edited it and which fields, so the detail dialog can
   *  show "Edited by X during review" instead of this only being visible
   *  in the audit log. Overwritten by each later Review, not a history
   *  list - same "last decision only" shape as approval/financeApproval. */
  reviewEdit?: ReviewEdit | null;
  /** Legacy: the trip's gross total in `currency`, on requests settled
   *  before Finance could pay out in several currencies. */
  settledAmount?: number | null;
  /** What Finance paid the employee, net of any advance, one line per
   *  currency paid (see requests_api.py's settle_request). */
  settledAmounts?: AdvanceAmount[] | null;
  settledAt?: string | null;
  /** Only on the single-request detail fetch: true when the signed-in user
   *  raised this request, so money owed can read "Owed by you" rather than
   *  "Owed by <name>". */
  viewerIsRequester?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** 'Resubmit' means a Manager (or Finance, standing in for the Manager
 *  stage on a no-manager advance, or Finance itself sending back an
 *  already-Manager-approved advance before paying it) sent the advance
 *  back to the employee to fix - see resubmitRequestedBy on the Advance
 *  interface below for which stage, and therefore which status it returns
 *  to once resubmitted ('Manager' -> Pending, 'Finance' -> Approved). */
export type AdvanceStatus = 'Pending' | 'Approved' | 'Rejected' | 'Paid' | 'Resubmit';

export interface AdvanceAmount {
  currency: string;
  amount: number;
}

export interface Advance {
  id: string;
  userId: string;
  /** Set on an Other reimbursement advance (no trip; its purpose - the
   *  advance's name - is flightDetails). */
  requestKind?: RequestKind | null;
  claimType?: OtherClaimType | null;
  /** Only present on the single-advance detail fetch (GET
   *  /api/advances/:id) that backs AdvanceDetailDialog. */
  requester?: RequesterInfo;
  /** One line per currency the employee asked for (multi-currency
   *  advances). Only the line in the trip's policy currency is capped; an
   *  advance from before this existed comes back as a single line (see
   *  db/advances_store.py's advance_amounts). Format with
   *  formatAdvanceAmounts (lib/advance.ts). */
  amounts: AdvanceAmount[];
  reason?: string | null;
  /** Same travel-detail shape as a travel request's Step 1 - an advance is
   *  raised for a specific upcoming trip, so linking it onto a matching
   *  request can auto-fill these fields (never the amount). */
  department: string;
  country: string;
  /** Domestic or International - decides which requests this advance can
   *  link to (only one of the same travel type). Always present in API
   *  responses (older advances get theirs inferred server-side). */
  travelType?: TravelType | null;
  /** Fixed Allowance or Reimbursement - the scheme this advance was capped
   *  against and the only scheme it can later link to (see
   *  requests_api.py's _advance_entitlement/_advance_matches_claim_method).
   *  Null on an advance raised before this choice existed, which can link
   *  to either scheme. */
  claimMethod?: ClaimMethod | null;
  /** Snapshotted at submission, same as a travel request's own
   *  designationAtSubmission - null on an advance raised before this was
   *  captured. */
  designationAtSubmission?: string | null;
  /** International only - the starting country. */
  originCountry?: string | null;
  /** Domestic only - where the trip starts and ends, within `country`. */
  startCity?: string | null;
  endCity?: string | null;
  departureAt: string;
  arrivalAt: string;
  flightDetails?: string | null;
  /** Same Finance Approver pick as a travel request's
   *  financeApproverIdAtSubmission - only meaningful when this advance
   *  had no reporting manager to route to at submission time. Name/email
   *  snapshotted alongside so the timeline can name the Finance stage. */
  financeApproverIdAtSubmission?: string | null;
  financeApproverNameAtSubmission?: string | null;
  financeApproverEmailAtSubmission?: string | null;
  status: AdvanceStatus;
  approval?: AdvanceApproval | null;
  /** Set when the Finance Approver rejected the advance after the Manager had
   *  already approved it (the Manager's own decision stays in `approval`). */
  financeDecision?: { approverId: string; approverName?: string | null; approverEmail?: string | null; decision: 'Rejected'; comment: string | null; decidedAt: string } | null;
  linkedRequestId?: string | null;
  /** Small display-friendly summary of the travel request linkedRequestId
   *  points to (own id/status/trip-route fields) - resolved server-side so
   *  a list row can show something readable instead of the bare id. Only
   *  present when linkedRequestId is set and that request could still be
   *  found; otherwise null. */
  linkedRequestSummary?: {
    id: string;
    status: TravelRequestStatus;
    travelType?: TravelType | null;
    country: string;
    originCountry?: string | null;
    startCity?: string | null;
    endCity?: string | null;
    departureAt: string;
  } | null;
  paidAt?: string | null;
  /** Set together with status === 'Resubmit' - mirrors
   *  TravelRequestSummary's own resubmitRequestedBy/resubmitComment. */
  resubmitRequestedBy?: 'Manager' | 'Finance' | null;
  resubmitComment?: string | null;
  /** Set by an approver's Review action - mirrors TravelRequestSummary's
   *  own reviewEdit. */
  reviewEdit?: ReviewEdit | null;
  /** Other reimbursements only - the planned date of the trip/errand this
   *  advance is for ("YYYY-MM-DD", like a bill's own date; today or later).
   *  Purely a record for the employee/approvers to see; never used for FX
   *  (the ledger keeps falling back to createdAt) or any matching/
   *  validation. Null for a travel advance, and for an Other advance raised
   *  before this field existed. */
  advanceDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdvanceApproval {
  advanceId: string;
  requesterId?: string | null;
  requester?: RequesterInfo;
  approverId: string | null;
  /** Approver's display name + email, snapshotted onto the approval record
   *  at creation time (see db/advances_store.py's _approver_identity) - a
   *  readable reference to who the approver was, not just a bare id. Null for
   *  an unassigned stage or a record raised before this was captured. */
  approverName?: string | null;
  approverEmail?: string | null;
  status: ApprovalStatus;
  /** The advance's own top-level status (AdvanceStatus) - unlike `status`
   *  above (this Manager approval's own decision, which deliberately stays
   *  "Pending" after a resubmit request - see
   *  db/advances_store.py's AdvanceApprovalStore._to_dict), this is the real
   *  state: "Resubmit" means the ball is in the employee's court, not the
   *  Manager's, even though `status` still reads "Pending". */
  requestStatus?: AdvanceStatus;
  /** Set on an Other reimbursement advance's row (the Trip column, like
   *  Approval's own requestKind/claimType - flightDetails is that
   *  advance's purpose, its name). */
  requestKind?: RequestKind | null;
  claimType?: OtherClaimType | null;
  flightDetails?: string | null;
  /** Trip fields (TripLike, lib/trip.ts) - absent/null on an Other advance,
   *  which has no trip. */
  country?: string | null;
  travelType?: TravelType | null;
  originCountry?: string | null;
  startCity?: string | null;
  endCity?: string | null;
  /** One line per currency requested - same shape as Advance's own amounts
   *  (lifted from a legacy single amount+currency doc on read, see
   *  advance_amounts() in db/advances_store.py). */
  amounts: AdvanceAmount[];
  comment?: string | null;
  decidedAt?: string | null;
  createdAt: string;
}

export type ApprovalStatus = 'Pending' | 'Approved' | 'Rejected';

/** Embedded 1:1 inside its travel request (see db/approvals_store.py) - it
 *  has no id of its own; travelRequestId is what identifies it. Shared
 *  shape for both the Manager's `approval` and the Approver's
 *  `financeApproval` - only which field on TravelRequestSummary it's
 *  attached to tells you which stage it is. */
export interface Approval {
  travelRequestId: string;
  requesterId?: string | null;
  requester?: RequesterInfo;
  approverId: string | null;
  /** Approver's display name + email, snapshotted onto the approval record
   *  at creation time (see db/approvals_store.py's approver_identity) - a
   *  readable reference to who the approver was, not just a bare id. Null for
   *  an unassigned stage or a record raised before this was captured. */
  approverName?: string | null;
  approverEmail?: string | null;
  /** True when this Manager stage was stamped automatically because the
   *  requester has no reporting manager (see db/approvals_store.py's
   *  create_auto_approved) - approverId is null and status is Approved. */
  autoApproved?: boolean;
  status: ApprovalStatus;
  /** The request's own top-level status (TravelRequestStatus) - unlike
   *  `status` above (this Manager approval's own decision, which
   *  deliberately stays "Pending" after a resubmit request - see
   *  db/approvals_store.py's ApprovalStore._to_dict), this is the real
   *  state: "ResubmitRequested" means the ball is in the employee's court,
   *  not the Manager's, even though `status` still reads "Pending". Narrowed
   *  to a required field on FinanceQueueRow below, which has carried it all
   *  along. */
  requestStatus?: TravelRequestStatus;
  /** Set on an Other reimbursement's row (the queues' Trip column).
   *  flightDetails is that request's purpose, its name. */
  requestKind?: RequestKind | null;
  claimType?: OtherClaimType | null;
  flightDetails?: string | null;
  /** Trip fields (TripLike, lib/trip.ts) - absent/null on an Other
   *  reimbursement, which has no trip. */
  country?: string | null;
  travelType?: TravelType | null;
  originCountry?: string | null;
  startCity?: string | null;
  endCity?: string | null;
  comment?: string | null;
  decidedAt?: string | null;
  createdAt: string;
}

/** A Finance-queue row also carries the request's own top-level status -
 *  needed to know when a request is Approved-but-not-yet-Completed (i.e.
 *  eligible for the settlement action) - and the MANAGER's own comment
 *  from the earlier approval stage (`comment` above is Finance's own, on
 *  this same row, and is null until Finance actually decides - see
 *  db/approvals_store.py's _to_dict_from_finance). */
export interface FinanceQueueRow extends Approval {
  requestStatus: TravelRequestStatus;
  managerComment?: string | null;
}

export type AllowancePreviewSource = 'designation' | 'default';

export interface AllowancePreviewFixed {
  claimMethod: 'fixed_allowance';
  travelType: TravelType;
  fullDayCount: number;
  halfDayCount: number;
  currency: string;
  perDayAmount: number;
  estimatedAllowanceAmount: number;
  source: AllowancePreviewSource;
  policyVersion: number;
}

export interface AllowancePreviewReimbursement {
  claimMethod: 'reimbursement';
  travelType: TravelType;
  fullDayCount: number;
  halfDayCount: number;
  currency: string;
  limits: CategoryLimits;
  estimatedAllowanceAmount: number;
  source: AllowancePreviewSource;
  policyVersion: number;
  /** Every currency this employee has a configured Reimbursement limit for
   *  (not just `currency` above, the trip's own country/currency) - each
   *  value is that currency's own per-category limits, unscaled by day
   *  count (see travel_duration.py's estimate_reimbursement_total for how
   *  to scale one). Lets a bill in a currency different from the trip's
   *  own still be checked against its own configured limit, e.g. the
   *  Review step's per-currency bills-total breakdown. */
  limitsByCurrency: Record<string, CategoryLimits>;
}

export type AllowancePreview = AllowancePreviewFixed | AllowancePreviewReimbursement;

export interface AuditLogEntry {
  id: string;
  actorId: string;
  /** Resolved server-side from actorId - the raw Entra object id is kept
   *  above for the expanded detail view, this is what the table displays. */
  actor: { displayName: string | null; email: string | null };
  action: string;
  /** Human-readable form of `action` (e.g. "Manager decision" for
   *  "approval.decided") - action itself is kept for filtering/detail. */
  actionLabel: string;
  entityType: string;
  entityId: string;
  /** Short human description of what was acted on, resolved server-side
   *  per entityType (e.g. "Ranjith Bharathi R - Singapore" for an
   *  advance/approval). Falls back to "type · id" if resolution fails. */
  entitySummary: string;
  details?: Record<string, unknown> | null;
  createdAt: string;
}
